/**
  Service to interact with ticket-holder-service
 */
import { HolderModel as Holder } from '../models/holder.model';
import { PartnerServiceInputModel } from '../models/partner.model';
import { AuthState } from '@okta/okta-auth-js'
import { Constants } from '../util/constants';
import { learnMore as learnMoreFormValues } from '../util/form-values';

export class HolderService {

  constructor(
    private auth: AuthState | null,
    private host: string,
  ) { }

  private fetchHelper = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const accessToken = this.auth?.accessToken?.accessToken;
    if (!accessToken) throw new Error(HOLDER_ERROR_AUTH_TOKEN)

    return fetch(`${this.host}${url}`, {
      credentials: 'include',
      mode: 'cors',
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json; charset=utf-8',
      }),
      ...options,
    });
  }

  private formatHolder = (json: any): Holder => {
    const {
      address2,
      company,
      contactName,
      dateOfBirth,
      learnMore,
      purchaseLocation,
      ...rest
    } = json;

    return {
      ...rest,
      // may come back as null, default to empty string to remove react controlled component warning
      address2: address2 || '',
      company: company || '',
      // we convert strings into arrays for easier add/remove
      dateOfBirth: dateOfBirth ? dateOfBirth.split('-') : ['', '', ''],
      learnMore: learnMore
        ? (learnMore as string).split(',').map(str => str.trim()).filter(str => learnMoreFormValues.map(({ value }) => value).includes(str))
        : [],
      purchaseLocation: purchaseLocation
        ? (purchaseLocation as string).split(',').map(str => str.trim())
        : [],
    };
  }

  public getValidationStatus: (clubCode: string, venueId: string) => Promise<Holder> =
    async (clubCode: string, venueId: string): Promise<Holder> => {
      const accessToken = this.auth?.accessToken?.accessToken;
      if (!accessToken) throw new Error(HOLDER_ERROR_AUTH_TOKEN)

      const response: Response = await fetch(`${this.host}/v1/account/validate/${clubCode}/${venueId}`, {
        method: 'GET',
        credentials: 'include', // pass jwt
        mode: 'cors',
        headers: new Headers({
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json; charset=utf-8',
        }),
      });

      if (response.ok) {
        if (response.status === 203)
          throw new Error(HOLDER_STATUS_VERIFY_EMAIL);
        else {
          const json = await response.json().catch(_ => {/* empty throws json parse exception */ });
          const formattedJson = this.formatHolder(json || {});
          return formattedJson;
        }
      } else if (response.status === 403) {
        throw new Error(HOLDER_STATUS_NOT_ENTITLED);
      } else {
        throw new Error(HOLDER_STATUS_GENERIC_ERROR);
      }

    };

  public getHolder: (clubCode: string, venueId: string) => Promise<Holder> =
    async (clubCode: string, venueId: string): Promise<Holder> => {
      const accessToken = this.auth?.accessToken?.accessToken;
      if (!accessToken) throw new Error(HOLDER_ERROR_AUTH_TOKEN)

      const response: Response = await fetch(`${this.host}/v1/account/${clubCode}/${venueId}`, {
        method: 'GET',
        credentials: 'include', // pass jwt
        mode: 'cors',
        headers: new Headers({
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json; charset=utf-8',
        }),
      });

      if (response.ok) {
        return response.json().catch(_ => {/* empty throws json parse exception */ });
      } else {
        throw new Error('Failed to get holder');
      }
    };

  public publishHolder = async (holder: Holder, isUpdate: boolean): Promise<boolean> => {
    const flatHolder = {
      ...holder,
      dateOfBirth: holder.dateOfBirth.join('-'),
      learnMore: holder.learnMore.join(),
    };

    const response = await this.fetchHelper('/v1/account', {
      method: isUpdate ? 'PUT' : 'POST',
      body: JSON.stringify(flatHolder),
    });

    if (response.ok) {
      return (response.status === 203 || response.status === 200);
    } else {
      throw new Error(`Failed to ${isUpdate ? 'update' : 'publish'} holder`);
    }
  };

  public searchAndValidatePartnerAccountByEmail = async (accountId: number, clubCode: string, venueId: string, email: string): Promise<{ firstName: string, lastName: string, partnerAccountId: number }> => {
    const response = await this.fetchHelper(`/v1/account/searchPartner/${accountId}/${clubCode}/${venueId}?email=${email}`);

    if (response.ok) {
      return response.json();
    } else {
      const { message = 'Failed to check email status.' } = await response.json().catch(_ => ({}));
      throw new Error(response.status === 404 ? PARTNER_NEW_EMAIL : message);
    }
  }

  public addPartner: (partner: PartnerServiceInputModel, isExistingAccount?: boolean) => Promise<Holder> =
    async (partner: PartnerServiceInputModel, isExistingAccount?: boolean): Promise<Holder> => {
      const response = await this.fetchHelper('/v1/account/partner', {
        method: isExistingAccount ? 'PUT' : 'POST',
        body: JSON.stringify(partner),
      });

      if (response.ok) {
        return this.formatHolder(await response.json());
      } else {
        const { message } = await response.json().catch(_ => ({}));
        throw new Error(message || Constants.FAILED_ADD_PARTNER);
      }
    };
}

export const HOLDER_STATUS_VERIFY_EMAIL: string = 'EmailNotVerified';
export const HOLDER_STATUS_NOT_ENTITLED: string = "NotEntitled";
export const HOLDER_STATUS_GENERIC_ERROR: string = "GenericError";
const HOLDER_ERROR_AUTH_TOKEN = "NoAuthToken";
export const PARTNER_NEW_EMAIL = 'NewPartnerEmail';
